/**
 * InicioLayout — a moldura do Início do portal do parceiro (PR 5).
 *
 * Computador (`xl`+, 1280px): duas colunas — esquerda com saudação, atalho do
 * Atendimento e Comunicados; direita com a coluna fixa de Notificações
 * (`sticky`, altura limitada à área visível, para a lista rolar por dentro).
 * Abaixo de `xl` (inclusive `lg`, 1024–1279px): uma coluna só, na ordem
 * saudação → Atendimento → notificações compactas → Comunicados; a coluna da
 * direita some.
 *
 * M8 (revisão final) — o corte era em `lg` (1024px): com a barra lateral do
 * portal (256px) e o `max-w-6xl`, a coluna de Comunicados sobrava só 332px
 * em 1024px, mais ESTREITA que a lateral de notificações (340px) — layout
 * ao contrário da prioridade. `xl` (1280px) só bifurca quando a coluna
 * principal já tem espaço de sobra; entre 1024–1279px o Início fica em UMA
 * coluna cheia, sem espremer.
 *
 * As duas versões das notificações ficam no DOM ao mesmo tempo (CSS esconde
 * uma delas) — é o que o plano pede e evita um hook de media query. Quem
 * testa a página consulta cada uma pelo próprio `data-testid`.
 *
 * O layout dá o título "Comunicados" e a `<section>`; o conteúdo (aviso de
 * pendências, post destacado, lista, estados) vem pronto no slot. Slot nulo →
 * sem seção: é assim que a página esconde uma tela sem permissão.
 *
 * Margens: `p-4` no celular = os 16 px combinados; `min-w-0` em cada coluna
 * para nada (tabela, vídeo, texto longo) abrir rolagem horizontal.
 */
import { Megaphone } from "lucide-react";
import { useId, type ReactNode } from "react";

export interface InicioLayoutProps {
  saudacao: ReactNode;
  atendimento?: ReactNode;
  notificacoes?: ReactNode;
  notificacoesCompactas?: ReactNode;
  comunicados?: ReactNode;
}

export function InicioLayout({
  saudacao,
  atendimento,
  notificacoes,
  notificacoesCompactas,
  comunicados,
}: InicioLayoutProps) {
  const idComunicados = useId();

  return (
    <div className="container mx-auto max-w-6xl p-4 sm:p-6 lg:p-8" data-testid="inicio-layout">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px] xl:gap-8">
        <div className="min-w-0 space-y-6" data-testid="inicio-coluna-principal">
          {saudacao}

          {atendimento}

          {notificacoesCompactas != null && (
            <div className="xl:hidden">{notificacoesCompactas}</div>
          )}

          {comunicados != null && (
            <section
              aria-labelledby={idComunicados}
              data-testid="inicio-comunicados"
              className="space-y-4"
            >
              <h2
                id={idComunicados}
                // S5 (revisão de design) — era `text-lg font-semibold`, o
                // MESMO 18/600 do h3 de cada cartão: com 27 cartões, o título
                // da seção virava "mais um". 20/700 com `tracking-tight` é a
                // voz do h1; a escala fica 30 → 20 → 18 → 16 → 14 → 12.
                className="flex items-center gap-2 text-xl font-bold tracking-tight text-emerald-900"
              >
                <Megaphone className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                Comunicados
              </h2>
              {comunicados}
            </section>
          )}
        </div>

        {notificacoes != null && (
          // 8rem = cabeçalho do portal (4rem) + `p-8` em cima + `p-8` de
          // respiro embaixo. S1 (revisão de design): era `top-4` com 6rem —
          // o painel nascia em y=97 (cabeçalho + p-8) mas grudava em y=81 ao
          // rolar, um pulo de 16 px a cada início de rolagem, e em repouso
          // terminava 1 px ALÉM do fim da janela. `top-8` = o mesmo `p-8` do
          // layout: repouso e fixo no mesmo lugar. A `main` do PortalLayout é
          // quem rola, e `self-start` impede o grid de esticar a coluna
          // (sticky esticado não gruda).
          <aside
            data-testid="inicio-coluna-lateral"
            className="hidden min-w-0 xl:sticky xl:top-8 xl:flex xl:max-h-[calc(100dvh-8rem)] xl:flex-col xl:self-start"
          >
            {notificacoes}
          </aside>
        )}
      </div>
    </div>
  );
}

export default InicioLayout;
